import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';

type TypographyToken = keyof typeof typography;

export const BASELINE_STYLE_ID = 'signup-baseline-style';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

/**
 * The last-week steps' own pieces: the day list, the "what counts" row and
 * the v9 week summary (two tiles and seven day cells), which has no web
 * design-system component. Every value is an existing code token; v9's
 * `background/highlight` and `background/success light` use the same-named
 * code tokens, and `background/success subtle` uses moss green 300.
 */
export function baselineCss(): string {
  return `
.signup-baseline__days { display: flex; flex-direction: column; margin: 0; padding: 0; list-style: none; }
.signup-baseline__skeleton-row { padding: ${spacing.sm}px 0; }
.signup-baseline__guide { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: ${spacing.s}px; margin: 0; padding: 0; list-style: none; }
.signup-baseline__equivalent { display: flex; flex-direction: column; align-items: center; gap: ${spacing.xs}px; color: ${colors.text.primary}; ${font('c2Regular')} }
.signup-baseline__equivalent-sublabel { color: ${colors.text.tertiary}; }
.signup-baseline__equals { align-self: center; color: ${colors.text.primary}; ${font('b3Bold')} }
.signup-baseline__equals::before { content: '='; }
.signup-baseline__guide-text { margin: 0; color: ${colors.text.primary}; ${font('b3Regular')} }
.signup-baseline__answer-row { display: flex; align-items: center; gap: ${spacing.m}px; padding: ${spacing.sm}px 0; border-bottom: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; }
.signup-baseline__answer-day { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.signup-baseline__answer-label { color: ${colors.text.primary}; ${font('b2Bold')} }
.signup-baseline__answer-date { color: ${colors.text.secondary}; ${font('c2Regular')} }
.signup-baseline__answer-choices { display: flex; flex-shrink: 0; gap: ${spacing.s}px; }
.signup-baseline__limit { margin: 0; color: ${colors.text.secondary}; ${font('c2Regular')} }
.signup-baseline__summary { display: flex; flex-direction: column; gap: ${spacing.lg}px; }
.signup-baseline__tiles { display: flex; gap: ${spacing.sm}px; }
.signup-baseline__tile { flex: 1; min-width: 0; display: flex; align-items: center; gap: ${spacing.sm}px; margin: 0; padding: ${spacing.sm}px; border: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; border-radius: ${radius.xl}px; color: ${colors.text.primary}; ${font('b2Regular')} }
.signup-baseline__badge { display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center; min-width: ${sizing.weekSummary.control}px; height: ${sizing.weekSummary.figureHeight}px; border-radius: ${radius.lg}px; ${font('h1Bold28')} }
.signup-baseline__badge[data-fill='use'] { background: ${colors.background.highlight}; }
.signup-baseline__badge[data-fill='free'] { background: ${colors.background.successLight}; }
.signup-baseline__summary-label { margin: 0; color: ${colors.text.primary}; ${font('b2Bold')} }
.signup-baseline__cells { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: ${spacing.s}px; margin: 0; padding: 0; list-style: none; }
.signup-baseline__cell { display: flex; flex-direction: column; align-items: center; gap: ${spacing.s}px; color: ${colors.text.secondary}; ${font('c2Regular')} }
.signup-baseline__cell-count { display: flex; align-items: center; justify-content: center; width: 100%; height: ${sizing.weekSummary.control}px; border-radius: ${radius.lg}px; color: ${colors.text.primary}; ${font('b3Bold')} }
.signup-baseline__cell-count[data-fill='use'] { background: ${colors.background.highlight}; }
.signup-baseline__cell-count[data-fill='free'] { background: ${colors.mossGreen['300']}; }
`;
}

/** Injects the baseline stylesheet once per document. No-op during prerender. */
export function ensureBaselineStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(BASELINE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = BASELINE_STYLE_ID;
  style.textContent = baselineCss();
  document.head.appendChild(style);
}
